import { useState, useRef, useEffect } from "react";
import { API } from "../../lib/api";
import { fileToDataUrl } from "../../lib/upload";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Send, Loader2, Paperclip, X, Lightbulb, TriangleAlert } from "lucide-react";
import { Logo } from "./Logo";

const stripMd = (s) => s.replace(/\*\*(.*?)\*\*/g, "$1").replace(/^#{1,4}\s*/gm, "").replace(/^[-•]\s*/gm, "");

export const parseBlocks = (content) => {
  const blocks = [];
  let text = [];
  const flushText = () => { if (text.length) { blocks.push({ type: "text", text: text.join("\n") }); text = []; } };
  for (const raw of content.split("\n")) {
    const line = raw.trimEnd();
    if (line.trimStart().startsWith(">>")) {
      const parts = line.trimStart().slice(2).split("|").map((p) => p.trim());
      const kind = (parts[0] || "").toUpperCase();
      if (kind === "STAT" && parts.length >= 3) {
        flushText();
        const last = blocks[blocks.length - 1];
        if (last?.type === "stats") last.items.push({ label: parts[1], value: parts.slice(2).join(" ") });
        else blocks.push({ type: "stats", items: [{ label: parts[1], value: parts.slice(2).join(" ") }] });
      } else if (kind === "ROW" && parts.length >= 3) {
        flushText();
        const last = blocks[blocks.length - 1];
        if (last?.type === "table") last.rows.push({ label: parts[1], value: parts.slice(2).join(" ") });
        else blocks.push({ type: "table", rows: [{ label: parts[1], value: parts.slice(2).join(" ") }] });
      } else if ((kind === "TIP" || kind === "WARN") && parts.length >= 2) {
        flushText();
        blocks.push({ type: kind.toLowerCase(), text: parts.slice(1).join(" ") });
      }
      // incomplete ">>" lines mid-stream are skipped to avoid marker flicker
    } else if (line.trim()) {
      text.push(stripMd(line));
    } else if (text.length) {
      text.push("");
    }
  }
  flushText();
  return blocks;
};

const AssistantBlocks = ({ content }) => {
  const blocks = parseBlocks(content);
  const cards = blocks.filter((b) => b.type === "table" || b.type === "tip" || b.type === "warn");
  const sideBySide = cards.length >= 2;
  return (
    <div className="space-y-3 w-full">
      {blocks.map((b, i) => {
        if (b.type === "text")
          return <p key={i} className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-block-text">{b.text}</p>;
        if (b.type === "stats")
          return (
            <div key={i} className="flex flex-wrap gap-2.5" data-testid="ai-block-stats">
              {b.items.map((s, j) => (
                <div key={j} className="rounded-xl border border-[var(--cc-accent)]/30 bg-[var(--cc-accent)]/5 px-4 py-3 min-w-[120px]">
                  <p className="font-mono-cc text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</p>
                  <p className="font-heading font-extrabold text-2xl mt-0.5 text-[var(--cc-accent)]">{s.value}</p>
                </div>
              ))}
            </div>
          );
        const inner = b.type === "table" ? (
          <div key={i} className="rounded-xl border overflow-hidden bg-background/40" data-testid="ai-block-table">
            {b.rows.map((r, j) => (
              <div key={j} className={`flex items-center justify-between gap-4 px-4 py-2.5 text-sm ${j > 0 ? "border-t" : ""}`}>
                <span className="text-muted-foreground">{r.label}</span>
                <span className="font-mono-cc font-semibold shrink-0">{r.value}</span>
              </div>
            ))}
          </div>
        ) : b.type === "tip" ? (
          <div key={i} className="rounded-xl border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 px-4 py-3 flex items-start gap-3" data-testid="ai-block-tip">
            <Lightbulb className="w-4 h-4 text-[var(--cc-accent)] shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed">{b.text}</p>
          </div>
        ) : (
          <div key={i} className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3" data-testid="ai-block-warn">
            <TriangleAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed">{b.text}</p>
          </div>
        );
        return sideBySide ? <div key={i} className="lg:inline-block lg:w-[calc(50%-6px)] lg:align-top lg:mr-3 last:mr-0">{inner}</div> : inner;
      })}
    </div>
  );
};

export const AIChat = ({ mode = "customer", sessionId, placeholder = "Ask CastleCorp AI...", className = "", intro, quickActions = [] }) => {
  const [messages, setMessages] = useState(intro ? [{ role: "assistant", content: intro }] : []);
  const [input, setInput] = useState("");
  const [photos, setPhotos] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const sidRef = useRef(sessionId || `${mode}-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    const converted = await Promise.all(files.map((f) => fileToDataUrl(f, 1280).catch(() => null)));
    setPhotos((p) => [...p, ...converted.filter(Boolean)].slice(0, 4));
    e.target.value = "";
  };

  const send = async (forcedText) => {
    const text = (forcedText ?? input).trim();
    if ((!text && photos.length === 0) || streaming) return;
    const outPhotos = photos;
    setInput("");
    setPhotos([]);
    setMessages((m) => [...m, { role: "user", content: text || "(photo attached)", photos: outPhotos }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: text || "Please analyze the attached photo(s).", mode, photos: outPhotos }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "I hit a temporary issue. Please try again." };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "Connection issue — please try again." }]);
    }
    setStreaming(false);
  };

  return (
    <div className={`flex flex-col ${className}`} data-testid="ai-chat-container">
      <div className="flex-1 overflow-y-auto space-y-4 p-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-50 gap-3 py-10">
            <Logo size={40} />
            <p className="text-sm text-muted-foreground">CastleCorp AI is standing by.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "user" ? (
              <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-[var(--cc-accent)] text-black font-medium">
                {(m.photos || []).length > 0 && (
                  <div className="flex gap-1.5 mb-2">
                    {m.photos.map((p, j) => <img key={j} src={p} alt="" className="w-14 h-14 object-cover rounded-lg border border-black/20" />)}
                  </div>
                )}
                {m.content}
              </div>
            ) : (
              <div className="max-w-[95%] w-fit rounded-2xl px-4 py-3 bg-secondary text-foreground border">
                {m.content ? <AssistantBlocks content={m.content} /> : <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {quickActions.length > 0 && (
        <div className="px-3 pb-1.5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="ai-quick-actions">
          {quickActions.map((qa) => (
            <button key={qa.label} data-testid={`ai-quick-${qa.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => send(qa.prompt)} disabled={streaming}
              className="shrink-0 rounded-full border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 px-4 py-2 text-xs font-semibold text-[var(--cc-accent)] hover:bg-[var(--cc-accent)]/15 transition-colors duration-200 disabled:opacity-50">
              {qa.label}
            </button>
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <div className="px-3 pb-1.5 flex gap-2" data-testid="ai-photo-strip">
          {photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={p} alt="" className="w-12 h-12 object-cover rounded-lg border" />
              <button onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))} data-testid={`ai-photo-remove-${i}`}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t p-3 flex gap-2 items-center">
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="ai-file-input" />
        <Button variant="outline" size="icon" className="shrink-0 rounded-full h-11 w-11" data-testid="ai-attach-button"
          onClick={() => fileRef.current?.click()} disabled={streaming} aria-label="Attach photos">
          <Paperclip className="w-4 h-4" />
        </Button>
        <Input
          data-testid="ai-chat-input"
          className="h-11 rounded-full"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={placeholder}
          disabled={streaming}
        />
        <Button data-testid="ai-chat-send-button" onClick={() => send()} disabled={streaming || (!input.trim() && photos.length === 0)} size="icon" className="btn-glow shrink-0 rounded-full h-11 w-11">
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};
